import { useCallback, useMemo } from 'react';
import { useCurrentLocale } from '@locales/client';
import { getLocalizedGamePath } from '@/shared/libs/get-localized-game-path';
import { getBonusGamesSet } from '@/shared/libs/get-bonus-games-set';
import {
  useIsAuth,
  useUserId,
} from '@/shared/providers/user-provider/use-user';
import {
  useIsBonusActive,
  useBonusInfoGames,
  useBonusInfoProvider,
} from '@/shared/providers/notifications-provider/store';
import type { GameCatalogItem } from '@/entities/game/model/types';

type AccessCheckGame = Pick<GameCatalogItem, 'id'> &
  Partial<Pick<GameCatalogItem, 'denyBonus' | 'providerId' | 'title' | 'code'>>;

export const useGameAccessCheck = () => {
  const userId = useUserId();
  const isAuth = useIsAuth();
  const isBonusActive = useIsBonusActive();
  const bonusGames = useBonusInfoGames();
  const bonusProvider = useBonusInfoProvider();
  const locale = useCurrentLocale();

  const hasBonus = useCallback(
    (game?: AccessCheckGame | null) => {
      return (
        (game ? getBonusGamesSet(bonusGames).has(game.id) : false) ||
        !!(!bonusGames && !bonusProvider && isBonusActive)
      );
    },
    [bonusGames, bonusProvider, isBonusActive]
  );

  const isDisabled = useCallback(
    (game?: AccessCheckGame | null) => {
      const denyBonus = game?.denyBonus;
      const hasGames = bonusGames?.length > 0;

      if (!userId) return false;

      if (isBonusActive && hasGames) {
        return !!(!hasBonus(game) || denyBonus);
      }

      return !!(isBonusActive && denyBonus);
    },
    [userId, isBonusActive, bonusGames, hasBonus]
  );

  const canOpenGame = useCallback(
    (game?: AccessCheckGame | null) => {
      if (!game) return false;
      const { denyBonus } = game;

      if (!isAuth) return true;
      if (denyBonus && isBonusActive) return false;

      return !isDisabled(game);
    },
    [isAuth, isBonusActive, isDisabled]
  );

  const getGameInfoPath = useCallback(
    (game?: AccessCheckGame | null) =>
      game ? getLocalizedGamePath(locale, game) : '',
    [locale]
  );
  const getGamePath = useCallback(
    (game?: AccessCheckGame | null, isDemo = false) =>
      game ? `${getGameInfoPath(game)}?enter=${isDemo ? 'demo' : 'play'}` : '',
    [getGameInfoPath]
  );

  return useMemo(
    () => ({
      canOpenGame,
      isDisabled,
      hasBonus,
      getGamePath,
      getGameInfoPath,
    }),
    [canOpenGame, isDisabled, hasBonus, getGamePath, getGameInfoPath]
  );
};
